import React from 'react';
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Grid,
  MenuItem,
  Paper,
  Stack,
  TextField,
  Typography,
} from '@mui/material';

import BudgetValidationCardsPanel from './BudgetValidationCardsPanel';
import BudgetValidationQueuePanel from './BudgetValidationQueuePanel';
import DecisionPanel from './DecisionPanel';

import {
  BudgetValidationCards,
  BudgetValidationQueueRow,
} from './types';

import {
  decideBudgetValidationItem,
  fetchBudgetValidationCards,
  fetchBudgetValidationQueue,
  generateBudgetValidationSession,
  runBudgetValidationRecommendations,
} from './budgetValidationApi';

import BudgetValidationFilterBar from './BudgetValidationFilterBar';


type State = {
  scenarioCode: string;
  sessionId: number | '';
  sessionName: string;
  pressureId: number | '';
  sourceCode: string;
  decisionCode: string;
  loading: boolean;
  error: string;
  success: string;
  cards: BudgetValidationCards | null;
  queue: BudgetValidationQueueRow[];
  selected: BudgetValidationQueueRow | null;

  scenarios: any[];
  sessions: any[];
  sources: any[];
  decisions: any[];
};

class BudgetValidationWorkbench extends React.Component<any, State> {
  state: State = {
    scenarioCode: 'DEMO01',
    sessionId: '',
    sessionName: 'DCG Budget Validation Session',
    pressureId: '',
    sourceCode: '',
    decisionCode: '',
    loading: false,
    error: '',
    success: '',
    cards: null,
    queue: [],
    selected: null,

    scenarios: [],
    sessions: [],
    sources: [],
    decisions: [],
  };

  componentDidMount() {
    this.loadLookups();
  }

  loadLookups = async () => {
    const scenarios = [{ shc: 'DEMO01', nam: 'Demo Scenario' }];
    const sessions: any[] = [];
    const sources = [
      { shc: 'ACTIVITY', nam: 'Activity Budget' },
      { shc: 'DIRECT', nam: 'Direct Budget' },
      { shc: 'INVESTMENT', nam: 'Investment Budget' },
    ];
    const decisions = [
      { shc: 'PENDING', nam: 'Pending' },
      { shc: 'APPROVED', nam: 'Approved' },
      { shc: 'REDUCED', nam: 'Reduced' },
      { shc: 'REJECTED', nam: 'Rejected' },
      { shc: 'HOLD', nam: 'Hold' },
      { shc: 'ESCALATED', nam: 'Escalated' },
    ];

    this.setState({ scenarios, sessions, sources, decisions });
  };

  onScenarioChange = (scenarioCode: string) => this.setState({ scenarioCode });
  onSessionChange = (sessionId: number | '') => this.setState({ sessionId }, () => {
    if (sessionId) this.load(Number(sessionId));
  });
  onSessionNameChange = (sessionName: string) => this.setState({ sessionName });
  onPressureIdChange = (pressureId: number | '') => this.setState({ pressureId });
  onSourceChange = (sourceCode: string) => this.setState({ sourceCode }, () => {
    if (this.state.sessionId) this.load(Number(this.state.sessionId));
  });
  onDecisionChange = (decisionCode: string) => this.setState({ decisionCode }, () => {
    if (this.state.sessionId) this.load(Number(this.state.sessionId));
  });

  setText = (
    name: 'scenarioCode' | 'sessionName' | 'sourceCode' | 'decisionCode'
  ) => (e: React.ChangeEvent<HTMLInputElement>) => {
    this.setState({
      [name]: e.target.value.toUpperCase(),
    } as Pick<State, 'scenarioCode' | 'sessionName' | 'sourceCode' | 'decisionCode'>);
  };

  setNumber = (name: 'sessionId' | 'pressureId') => (
    e: React.ChangeEvent<HTMLInputElement>
  ) => {
    const value = e.target.value === '' ? '' : Number(e.target.value);
    this.setState({ [name]: value } as Pick<State, 'sessionId' | 'pressureId'>);
  };

  generate = async () => {
    const { scenarioCode, pressureId, sessionName } = this.state;

    this.setState({ loading: true, error: '', success: '' });

    try {
      const res = await generateBudgetValidationSession(
        scenarioCode,
        pressureId === '' ? null : Number(pressureId),
        sessionName
      );

      const sid = Number(res.rid || res.sd?.rid || res.result?.rid);

      this.setState({
        sessionId: sid,
        success: `Validation session generated with ${res.rc || 0} queue item(s).`,
      });

      await this.load(sid);
    } catch (err: any) {
      this.setState({ error: err.message || String(err) });
    } finally {
      this.setState({ loading: false });
    }
  };

  recommend = async () => {
    const { sessionId } = this.state;

    if (!sessionId) {
      this.setState({ error: 'Generate or enter a session ID first.' });
      return;
    }

    this.setState({ loading: true, error: '', success: '' });

    try {
      await runBudgetValidationRecommendations(Number(sessionId));
      this.setState({ success: 'Validation recommendations generated.' });
      await this.load(Number(sessionId));
    } catch (err: any) {
      this.setState({ error: err.message || String(err) });
    } finally {
      this.setState({ loading: false });
    }
  };

  load = async (sid?: number) => {
    const { sessionId, sourceCode, decisionCode } = this.state;
    const target = sid || Number(sessionId);

    if (!target) {
      this.setState({ error: 'Enter a session ID or generate a new session.' });
      return;
    }

    this.setState({ loading: true, error: '' });

    try {
      const [cards, queue] = await Promise.all([
        fetchBudgetValidationCards(target),
        fetchBudgetValidationQueue(target, sourceCode, decisionCode),
      ]);

      this.setState({
        cards,
        queue,
        selected: queue.length > 0 ? queue[0] : null,
      });
    } catch (err: any) {
      this.setState({ error: err.message || String(err) });
    } finally {
      this.setState({ loading: false });
    }
  };

  decide = async (decisionCode: string, amount: number, reason: string) => {
    const { selected, sessionId } = this.state;

    if (!selected) return;

    this.setState({ loading: true, error: '', success: '' });

    try {
      await decideBudgetValidationItem({
        rid: selected.rid,
        dcc: decisionCode,
        amt: amount,
        dcr: reason,
      });

      this.setState({ success: `${decisionCode} decision saved.` });
      await this.load(Number(sessionId));
    } catch (err: any) {
      this.setState({ error: err.message || String(err) });
    } finally {
      this.setState({ loading: false });
    }
  };

  render() {
    const {
      scenarioCode,
      sessionId,
      sessionName,
      pressureId,
      sourceCode,
      decisionCode,
      loading,
      error,
      success,
      cards,
      queue,
      selected,
    } = this.state;

    return (
      <Box sx={{ p: 3 }}>
        <Stack
          direction={{ xs: 'column', md: 'row' }}
          justifyContent="space-between"
          alignItems={{ xs: 'stretch', md: 'center' }}
          spacing={2}
          sx={{ mb: 3 }}
        >
          <Box>
            <Typography variant="h4" sx={{ fontWeight: 700 }}>
              Budget Validation Center
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Validate budget requests against economic capacity, scoring rules and management decision controls.
            </Typography>
          </Box>

          <Stack direction="row" spacing={1}>
            <Button variant="outlined" onClick={() => this.load()} disabled={loading}>
              Refresh
            </Button>
            <Button variant="outlined" onClick={this.recommend} disabled={loading}>
              Recommend
            </Button>
            <Button variant="contained" onClick={this.generate} disabled={loading}>
              Generate Session
            </Button>
          </Stack>
        </Stack>

        <BudgetValidationFilterBar
          scenarioCode={scenarioCode}
          sessionId={sessionId}
          sessionName={sessionName}
          pressureId={pressureId}
          sourceCode={sourceCode}
          decisionCode={decisionCode}
          scenarios={this.state.scenarios}
          sessions={this.state.sessions}
          sources={this.state.sources}
          decisions={this.state.decisions}
          loading={loading}
          onScenarioChange={this.onScenarioChange}
          onSessionChange={this.onSessionChange}
          onSessionNameChange={this.onSessionNameChange}
          onPressureIdChange={this.onPressureIdChange}
          onSourceChange={this.onSourceChange}
          onDecisionChange={this.onDecisionChange}
          onGenerate={this.generate}
          onRecommend={this.recommend}
          onRefresh={() => this.load()}
        />

        {/* <Paper elevation={1} sx={{ p: 2, mb: 3 }}>

          <Grid container spacing={2}>
            <Grid size={{ xs: 12, md: 2 }}>
              <TextField label="Scenario" value={scenarioCode} onChange={this.setText('scenarioCode')} fullWidth />
            </Grid>
            <Grid size={{ xs: 12, md: 2 }}>
              <TextField label="Session ID" value={sessionId} onChange={this.setNumber('sessionId')} fullWidth />
            </Grid>
            <Grid size={{ xs: 12, md: 2 }}>
              <TextField label="Pressure ID" value={pressureId} onChange={this.setNumber('pressureId')} placeholder="Optional" fullWidth />
            </Grid>
            <Grid size={{ xs: 12, md: 3 }}>
              <TextField label="Session Name" value={sessionName} onChange={this.setText('sessionName')} fullWidth />
            </Grid>
            <Grid size={{ xs: 12, md: 1.5 }}>
              <TextField select label="Source" value={sourceCode} onChange={this.setText('sourceCode')} fullWidth>
                <MenuItem value="">All</MenuItem>
                <MenuItem value="ACTIVITY">Activity</MenuItem>
                <MenuItem value="DIRECT">Direct</MenuItem>
                <MenuItem value="INVESTMENT">Investment</MenuItem>
              </TextField>
            </Grid>
            <Grid size={{ xs: 12, md: 1.5 }}>
              <TextField select label="Decision" value={decisionCode} onChange={this.setText('decisionCode')} fullWidth>
                <MenuItem value="">All</MenuItem>
                <MenuItem value="PENDING">Pending</MenuItem>
                <MenuItem value="APPROVED">Approved</MenuItem>
                <MenuItem value="REDUCED">Reduced</MenuItem>
                <MenuItem value="REJECTED">Rejected</MenuItem>
                <MenuItem value="HOLD">Hold</MenuItem>
                <MenuItem value="ESCALATED">Escalated</MenuItem>
              </TextField>
            </Grid>
          </Grid>
        </Paper> */}

        {error && <Alert severity="error" sx={{ mb: 3 }}>{error}</Alert>}
        {success && <Alert severity="success" sx={{ mb: 3 }}>{success}</Alert>}

        {loading && (
          <Stack direction="row" spacing={2} alignItems="center" sx={{ mb: 3 }}>
            <CircularProgress size={22} />
            <Typography>Processing budget validation...</Typography>
          </Stack>
        )}

        <Stack spacing={3}>
          <BudgetValidationCardsPanel cards={cards} />

          <Grid container spacing={3}>
            <Grid size={{ xs: 12, lg: 8 }}>
              <BudgetValidationQueuePanel
                rows={queue}
                selectedId={selected?.rid}
                loading={loading}
                onSelect={(selected) => this.setState({ selected })}
              />
            </Grid>

            <Grid size={{ xs: 12, lg: 4 }}>
              <DecisionPanel selected={selected} onDecide={this.decide} />
            </Grid>
          </Grid>
        </Stack>
      </Box>
    );
  }
}

export default BudgetValidationWorkbench;
